import firebaseApp from '@/app/database/firebase-app';
import axios from 'axios';
import { getAuth } from 'firebase/auth';

export interface StudyGuideOptions {
  contentSource: 'all' | 'flagged';
  customPrompt: string;
  selectedSuggestions: string[];
  complexityLevel: 'novice' | 'intermediate' | 'expert';
  studyGuideLength: 'quick' | 'standard' | 'comprehensive';
}

axios.defaults.headers.common.Accept = 'application/json';
axios.defaults.timeout = 0;

const getStudyGuide = async (deckId: string, language?: string) => {
  const auth = getAuth(firebaseApp);

  try {
    return (
      await axios.get<string>('/api/studyGuide', {
        headers: { token: await auth.currentUser.getIdToken() },
        timeout: 0,
        params: { deckId, language }
      })
    ).data;
  } catch (e) {
    return (
      await axios.get<string>(`/external/api/studyGuide`, {
        headers: { authorization: await auth.currentUser.getIdToken() },
        timeout: 0,
        params: { deckId }
      })
    ).data;
  }
};

const getSuggestions = async (deckId: string, contentSource: 'all' | 'flagged' = 'all'): Promise<string[]> => {
  const auth = getAuth(firebaseApp);

  try {
    const response = await axios.post<{ suggestions: string[] }>('/api/studyGuideSuggestions', {
      deckId,
      token: await auth.currentUser.getIdToken(),
      contentSource
    });
    return response.data.suggestions;
  } catch (error) {
    console.error('Error getting suggestions:', error);
    // Return fallback suggestions
    return [
      "Key Concepts and Definitions",
      "Main Topics and Themes",
      "Important Formulas and Methods",
      "Core Principles and Rules", 
      "Essential Facts and Details",
      "Critical Processes and Steps"
    ];
  }
};

const getCustomStudyGuide = async (deckId: string, options: StudyGuideOptions): Promise<string> => {
  const auth = getAuth(firebaseApp);

  try {
    const response = await axios.post<{ content: string }>('/api/studyGuideCustom', {
      deckId,
      token: await auth.currentUser.getIdToken(),
      options
    });
    return response.data.content;
  } catch (error) {
    console.error('Error generating custom study guide:', error);
    throw error;
  }
};

export default {
  getStudyGuide,
  getSuggestions,
  getCustomStudyGuide
};
